import type {
  ColumnFiltersState,
  PaginationState,
  SortingState,
} from '@tanstack/react-table'
import { Spin } from 'antd'
import { Box } from 'designSystem/components/Box'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from 'designSystem/components/Resizable'
import { DocumentPreviewPanel } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentPreview/DocumentPreviewPanel'
import { DocumentCardFilterBar } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/documentCardFilterBar/DocumentCardFilterBar'
import { DocumentResultsList } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/DocumentResultsList'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import type { UseDocumentResultsInteractionsResult } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentResultsInteractions'
import type { UseDocumentResultsViewStateResult } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentResultsViewState'
import {
  NoResults,
  PreviewPane,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/advancedSearchStyles'
import { GeckoPowerTable } from 'features/knowledgeDocumentViewer/components/table/GeckoPowerTable'
import type { ViewMode } from 'features/knowledgeDocumentViewer/types'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import type { Dispatch, ReactElement, SetStateAction } from 'react'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { DocumentResultsFooter } from './DocumentResultsFooter'
import { SearchTableWrapper } from './SearchTableWrapper'

const ResizablePanelContent = styled.div`
  display: flex;
  height: 100%;
  min-height: 0;
  min-width: 0;
`

export interface DocumentResultsContentProps {
  data: {
    documentResults: DocumentTableRow[]
    documentTotalCount: number
    searchedSystemDocuments: SystemDocumentEntry[] | undefined
    isLoadingDocuments: boolean
  }
  interactions: UseDocumentResultsInteractionsResult
  isLinkedDocsMode: boolean
  layout: {
    leftPaneWidthPercent: number
    setLeftPaneWidthPercent: Dispatch<SetStateAction<number>>
  }
  headerContent?: React.ReactNode
  showCardFilterBar: boolean
  tableState: {
    pagination: PaginationState
    setPagination: (
      updater: PaginationState | ((prev: PaginationState) => PaginationState)
    ) => void
    sorting: SortingState
    setSorting: (
      updater: SortingState | ((prev: SortingState) => SortingState)
    ) => void
    tableFilters: ColumnFiltersState | undefined
  }
  viewState: UseDocumentResultsViewStateResult
  viewMode: ViewMode
}

export function DocumentResultsContent({
  data,
  interactions,
  isLinkedDocsMode,
  layout,
  headerContent,
  showCardFilterBar,
  tableState,
  viewState,
  viewMode,
}: DocumentResultsContentProps): ReactElement {
  const {
    documentResults,
    documentTotalCount,
    searchedSystemDocuments,
    isLoadingDocuments,
  } = data
  const {
    handleAddToContext,
    handleRowAuxClick,
    handleRowClick,
    handleRowContextMenu,
  } = interactions
  const { pagination, setPagination, sorting, setSorting, tableFilters } =
    tableState
  const {
    activeIndex,
    cardFilters,
    commitCardFiltersToUrl,
    contentAreaRef,
    documentPreviewPanelProps,
    filteredDocuments,
    handleCardFiltersChange,
    handleRowFocus,
    handleTableFilteredDataChange,
    hasActiveFilters,
    isPreviewOpen,
    onViewModeChange,
    onSearchDocumentNavigate,
    setActiveIndex,
    tableColumns,
    tableFilteredCount,
    tableHasActiveFilters,
  } = viewState

  const hasNoCardResults =
    viewMode === 'card' &&
    documentResults.length === 0 &&
    searchedSystemDocuments?.length === 0
  const showDocumentResultsFooter = !isLoadingDocuments && !hasNoCardResults
  const showPreviewLayout = showDocumentResultsFooter && isPreviewOpen
  const { leftPaneWidthPercent, setLeftPaneWidthPercent } = layout
  const defaultPreviewLayout = useMemo(
    () => ({
      'document-results': leftPaneWidthPercent,
      'document-preview': 100 - leftPaneWidthPercent,
    }),
    [leftPaneWidthPercent]
  )
  const handlePreviewLayout = useCallback(
    (nextLayout: Record<string, number>) => {
      const nextLeftPaneWidth = nextLayout['document-results']
      if (typeof nextLeftPaneWidth === 'number') {
        setLeftPaneWidthPercent(nextLeftPaneWidth)
      }
    },
    [setLeftPaneWidthPercent]
  )

  const resultsFooter = showDocumentResultsFooter ? (
    <DocumentResultsFooter
      activeIndex={activeIndex}
      documentTotalCount={documentTotalCount}
      filteredDocuments={filteredDocuments}
      hasActiveFilters={hasActiveFilters}
      pagination={pagination}
      setActiveIndex={setActiveIndex}
      setPagination={setPagination}
      tableScrollContainerRef={contentAreaRef}
      tableFilteredCount={tableFilteredCount}
      tableHasActiveFilters={tableHasActiveFilters}
      viewMode={viewMode}
      onViewModeChange={onViewModeChange}
    />
  ) : null

  const resultsPane = (
    <Box
      display='flex'
      direction='column'
      flex='one'
      minWidth='none'
      minHeight='none'
      overflow='hidden'>
      {headerContent}
      <Box
        display='flex'
        direction='column'
        flex='one'
        minHeight='none'
        overflow='hidden'
        ref={(node) => {
          contentAreaRef.current = node
        }}
        tabIndex={-1}>
        {isLoadingDocuments ? (
          <Box
            display='flex'
            align='center'
            justify='center'
            padding='2xl'
            flex='one'>
            <Spin />
          </Box>
        ) : hasNoCardResults ? (
          <NoResults>No documents found</NoResults>
        ) : (
          <Box
            display='flex'
            direction='column'
            flex='one'
            minWidth='none'
            minHeight='none'
            overflow={viewMode === 'card' ? 'yAuto' : 'hidden'}>
            {viewMode === 'card' ? (
              <>
                {showCardFilterBar ? (
                  <DocumentCardFilterBar
                    documents={documentResults}
                    filters={cardFilters}
                    onFiltersChange={handleCardFiltersChange}
                    onFilterCommit={commitCardFiltersToUrl}
                  />
                ) : null}
                <DocumentResultsList
                  filteredDocuments={filteredDocuments}
                  totalCount={documentTotalCount}
                  filteredCount={filteredDocuments.length}
                  hasActiveFilters={hasActiveFilters}
                  activeIndex={activeIndex}
                  onSelect={(doc) =>
                    onSearchDocumentNavigate(doc, false, isLinkedDocsMode)
                  }
                  onActiveIndexChange={setActiveIndex}
                />
              </>
            ) : (
              <SearchTableWrapper>
                <GeckoPowerTable
                  cardStyleRows={false}
                  columns={tableColumns}
                  data={documentResults}
                  isLoadingData={isLoadingDocuments}
                  footerName='Documents'
                  pagination={pagination}
                  setPagination={setPagination}
                  sorting={sorting}
                  setSorting={setSorting}
                  showTable={true}
                  initialColumnFilters={tableFilters ?? []}
                  onFilteredDataChange={handleTableFilteredDataChange}
                  onRowClick={handleRowClick}
                  onRowAuxClick={handleRowAuxClick}
                  onRowContextMenu={handleRowContextMenu}
                  onRowFocus={handleRowFocus}
                  showFilterBar={true}
                  disableUrlFilters={true}
                  showFooter={false}
                  showPagination={false}
                  isTableHeaderSticky={true}
                  showColumnConfig={false}
                  cellPadding='.25rem'
                  cellFontSize='smaller'
                  borderSpacing='0'
                  getRowId={(row) => row.id}
                  getRowHtmlAttributes={(row) =>
                    ({
                      'data-row-id': row.id,
                    }) as React.HTMLAttributes<HTMLTableRowElement>
                  }
                />
              </SearchTableWrapper>
            )}
          </Box>
        )}
      </Box>
      {resultsFooter}
    </Box>
  )

  return (
    <Box
      display='flex'
      direction='column'
      flex='one'
      minHeight='none'
      overflow='hidden'>
      <Box
        display='flex'
        direction='row'
        flex='one'
        minHeight='none'
        overflow='hidden'>
        {showPreviewLayout ? (
          <ResizablePanelGroup
            orientation='horizontal'
            defaultLayout={defaultPreviewLayout}
            onLayoutChanged={handlePreviewLayout}>
            <ResizablePanel id='document-results' minSize='20%' maxSize='80%'>
              <ResizablePanelContent>{resultsPane}</ResizablePanelContent>
            </ResizablePanel>
            <ResizableHandle variant='hover' />
            <ResizablePanel id='document-preview' minSize='20%' maxSize='80%'>
              <ResizablePanelContent>
                <PreviewPane>
                  <DocumentPreviewPanel
                    {...documentPreviewPanelProps}
                    onAddToContext={handleAddToContext}
                  />
                </PreviewPane>
              </ResizablePanelContent>
            </ResizablePanel>
          </ResizablePanelGroup>
        ) : (
          resultsPane
        )}
      </Box>
    </Box>
  )
}
